/**
 * Lo que comparten Integraciones («Importar») y Oportunidades («Actualizar
 * ahora»): el estado de una sincronización encolada y cómo contarla en pantalla.
 * Espejo de `packages/backend/src/services/hubspot-sync-queue.service.ts`.
 */
export type HubspotEntity = 'companies' | 'contacts' | 'deals';
export type HubspotEntityStatus = 'SUCCESS' | 'PARTIAL' | 'FAILED' | 'SKIPPED';

export interface HubspotEntityOutcome {
  entity: HubspotEntity;
  status: HubspotEntityStatus;
  totalRecords: number;
  successCount: number;
  failedCount: number;
  error: string | null;
}

export type HubspotSyncJobState = 'queued' | 'running' | 'done' | 'failed' | 'unknown';

export interface HubspotSyncJobStatus {
  jobId: string;
  state: HubspotSyncJobState;
  entities: HubspotEntity[];
  outcomes: HubspotEntityOutcome[] | null;
  error: string | null;
  enqueuedAt: string | null;
  finishedAt: string | null;
}

export const ENTITY_LABEL: Record<HubspotEntity, string> = {
  companies: 'Empresas',
  contacts: 'Contactos',
  deals: 'Oportunidades',
};

export class SyncWaitTimeoutError extends Error {
  constructor() {
    super('La sincronización sigue en curso. Revisá de nuevo en unos minutos.');
    this.name = 'SyncWaitTimeoutError';
  }
}

export class SyncAbortedError extends Error {
  constructor() {
    super('La espera de la sincronización se canceló.');
    this.name = 'SyncAbortedError';
  }
}

/** Fallos seguidos del sondeo que se toleran antes de rendirse (red que parpadea). */
const MAX_POLL_FAILURES = 3;

const TERMINAL: HubspotSyncJobState[] = ['done', 'failed', 'unknown'];

export interface WaitOptions {
  intervalMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  onUpdate?: (s: HubspotSyncJobStatus) => void;
  signal?: AbortSignal;
}

export async function waitForSyncJob(
  fetchStatus: () => Promise<HubspotSyncJobStatus>,
  opts: WaitOptions = {}
): Promise<HubspotSyncJobStatus> {
  const intervalMs = opts.intervalMs ?? 2_000;
  const timeoutMs = opts.timeoutMs ?? 10 * 60_000;
  const signal = opts.signal;
  const sleep =
    opts.sleep ??
    ((ms: number) =>
      new Promise<void>((resolve) => {
        // Ya abortada: el bucle lo notará en la próxima vuelta; no dejar un timer colgado.
        if (signal?.aborted) return resolve();
        const timer = setTimeout(done, ms);
        function done() {
          clearTimeout(timer);
          signal?.removeEventListener('abort', done);
          resolve();
        }
        signal?.addEventListener('abort', done, { once: true });
      }));
  const now = opts.now ?? Date.now;
  const start = now();
  let failures = 0;
  for (;;) {
    if (signal?.aborted) throw new SyncAbortedError();
    let s: HubspotSyncJobStatus | null = null;
    try {
      s = await fetchStatus();
      failures = 0;
    } catch (err) {
      if (signal?.aborted) throw new SyncAbortedError();
      failures += 1;
      if (failures > MAX_POLL_FAILURES) throw err;
    }
    if (s) {
      opts.onUpdate?.(s);
      if (TERMINAL.includes(s.state)) return s;
    }
    if (now() - start >= timeoutMs) throw new SyncWaitTimeoutError();
    await sleep(intervalMs);
  }
}

function records(n: number): string {
  return n === 1 ? '1 registro actualizado' : `${n} registros actualizados`;
}

export function describeOutcome(o: HubspotEntityOutcome): string {
  const label = ENTITY_LABEL[o.entity];
  if (o.status === 'SKIPPED') return `${label}: apagado en la configuración`;
  if (o.status === 'FAILED') return `${label}: falló (${o.error ?? 'error desconocido'})`;
  // Una corrida incremental sin novedades relee el borde: no es «1 registro actualizado».
  if (o.successCount === 0 && o.failedCount === 0) return `${label}: sin cambios`;
  const withErrors = o.failedCount > 0 ? `, ${o.failedCount} con error` : '';
  return `${label}: ${records(o.successCount)}${withErrors}`;
}

export function summarizeSyncJob(s: HubspotSyncJobStatus): {
  title: string;
  description: string;
  variant: 'success' | 'warning' | 'destructive';
} {
  if (s.state === 'failed') {
    return {
      title: 'No se pudo sincronizar',
      description: s.error ?? 'Probá de nuevo en unos minutos.',
      variant: 'destructive',
    };
  }
  if (s.state !== 'done' || !s.outcomes) {
    return {
      title: 'No se pudo confirmar la sincronización',
      description: 'Revisá el registro en Integraciones.',
      variant: 'warning',
    };
  }
  const ran = s.outcomes.filter((o) => o.status !== 'SKIPPED');
  const failed = ran.filter((o) => o.status === 'FAILED');
  const description = s.outcomes.map(describeOutcome).join(' · ');
  if (failed.length === 0)
    return { title: 'Sincronización completada', description, variant: 'success' };
  if (failed.length === ran.length) {
    return { title: 'La sincronización falló', description, variant: 'destructive' };
  }
  return { title: 'Sincronización con errores', description, variant: 'warning' };
}
